import React, { useMemo, useState } from 'react';
import { Btn, Select } from '../../kit';
import { AdminLink } from '../../adminNav';
import {
  CtaCell, CtaClicks, ModePill, N, Pct, arr, categoryWord, ctaOf, modeWord, num, pctText, rateOf, str, weekEnd,
  weekLabel,
} from './shared';

/**
 * The MEMAC analytics table (owner, 2026-10-11: "Weekly, # of emails sent by
 * template, primary CTA, manual vs auto, open rates, click rates"): one row
 * per week × template (× manual/auto), newest week first, each week opened by
 * its totals row. The grid view turns it round — a template per row, a week
 * per column, one measure at a time — for reading a template's trend.
 *
 * Rows come from email/stats_weekly; the totals from its totals_by_week, or
 * worked out from the rows when the server sent none.
 */

export type WeeklyRow = Record<string, any> & { week: string; template_key: string };
type WeekHead = { week: string; label?: string; partial?: boolean };

/** A week's totals out of its rows: open rate over the HTML mails only (a
 *  plain-text mail can never count as opened, so it would drag the rate). */
export const sumWeek = (rows: WeeklyRow[]) => {
  let sent = 0, delivered = 0, opened = 0, clicked = 0, ctaClicks = 0, htmlDelivered = 0, htmlOpened = 0;
  let autoOpened = 0, botClicked = 0;
  rows.forEach(r => {
    sent += num(r.sent); delivered += num(r.delivered); opened += num(r.opened); clicked += num(r.clicked);
    ctaClicks += num(r.cta_clicks); autoOpened += num(r.auto_opened); botClicked += num(r.bot_clicked);
    // The server's HTML-only counts when it sends them (a row can mix HTML and
    // unknown sends); else the whole row when its open rate is defined.
    if (r.html_delivered != null) { htmlDelivered += num(r.html_delivered); htmlOpened += num(r.html_opened); }
    else if (rateOf(r, 'open_rate') !== null) { htmlDelivered += num(r.delivered); htmlOpened += num(r.opened); }
  });
  return {
    sent, delivered, opened, clicked, cta_clicks: ctaClicks, auto_opened: autoOpened, bot_clicked: botClicked,
    open_rate: htmlDelivered ? htmlOpened / htmlDelivered : (delivered ? null : undefined),
    click_rate: delivered ? clicked / delivered : undefined,
    ctor: opened ? clicked / opened : (delivered && !htmlDelivered ? null : undefined),
  } as Record<string, any>;
};

const COLS = ['Template', 'Category', 'Primary CTA', 'Mode', 'Sent', 'Delivered', 'Open rate', 'Click rate', 'CTOR',
  'CTA clicks', 'Auto opens', 'Bot clicks'];
const COL_HINT: Record<string, string> = {
  'Auto opens': 'Sends opened only by machines: instant reads, Apple MPP prefetches, scanners',
  'Bot clicks': 'Sends clicked only by link scanners',
};

const WeeklyTable: React.FC<{
  data: any; from?: string; to?: string;
  tplName: (k: string) => string;
  /** Narrow the whole dashboard to one template. */
  onTemplate?: (k: string) => void;
}> = ({ data, from, to, tplName, onTemplate }) => {
  const [layout, setLayout] = useState<'weeks' | 'grid'>('weeks');
  const [measure, setMeasure] = useState('sent');
  const rows: WeeklyRow[] = useMemo(() => arr(data?.rows).filter((r: any) => r && r.week), [data]);
  const totals: any[] = arr(data?.totals_by_week);

  // Every week in the period, newest first — weeks with nothing sent included,
  // so a quiet week shows as one rather than vanishing.
  const weeks: WeekHead[] = useMemo(() => {
    const seen = new Map<string, WeekHead>();
    arr(data?.weeks).forEach((w: any) => {
      const k = str(w?.week ?? w).slice(0, 10);
      if (k) seen.set(k, { week: k, label: str(w?.label) || undefined,
        partial: typeof w?.partial === 'boolean' ? w.partial : undefined });
    });
    rows.forEach(r => { const k = r.week.slice(0, 10); if (!seen.has(k)) seen.set(k, { week: k }); });
    return Array.from(seen.values()).sort((a, b) => b.week.localeCompare(a.week));
  }, [data, rows]);

  const byWeek = useMemo(() => {
    const m = new Map<string, WeeklyRow[]>();
    rows.forEach(r => {
      const k = r.week.slice(0, 10);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    });
    m.forEach(list => list.sort((a, b) => num(b.sent) - num(a.sent)));
    return m;
  }, [rows]);
  const totalOf = (w: string) => {
    const t = totals.find(x => str(x?.week).slice(0, 10) === w);
    const mine = sumWeek(byWeek.get(w) || []);
    return t ? { ...mine, ...t } : mine;
  };
  const partial = (w: string) => {
    const said = weeks.find(x => x.week === w)?.partial;
    if (said !== undefined) return said;
    return (from ? w < from : false) || (to ? weekEnd(w) > to : false);
  };
  const nameOf = (r: any) => str(r.template_name) || str(r.name) || tplName(r.template_key);
  // A campaign written for one send is its own line per campaign: link it.
  const campaignLink = (r: any) => (r.send_id ? (
    <AdminLink to={{ group: 'email', tab: 'campaigns', campaign: str(r.send_id) }}
      title="Open this campaign in Campaigns"
      className="ml-1 text-[11px] text-primary-700 underline underline-offset-2">campaign</AdminLink>
  ) : null);

  const downloadCsv = () => {
    const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const head = ['Week (Monday, IST)', 'Template', 'Template key', 'Category', 'Primary CTA', 'CTA link', 'Mode',
      'Sent', 'Delivered', 'Opened', 'Clicked', 'CTA clicks', 'Open rate', 'Click rate', 'CTOR', 'Auto opens (only)',
      'Bot clicks (only)'];
    const lines = [head.map(esc).join(',')];
    weeks.forEach(w => (byWeek.get(w.week) || []).forEach(r => {
      const c = ctaOf(r);
      lines.push([w.week, nameOf(r), r.template_key, categoryWord(r.category), c.label, c.url, modeWord(r.mode),
        num(r.sent), num(r.delivered), num(r.opened), num(r.clicked), r.cta_clicks ?? '',
        pctText(rateOf(r, 'open_rate')), pctText(rateOf(r, 'click_rate')), pctText(rateOf(r, 'ctor')),
        num(r.auto_opened), num(r.bot_clicked)].map(esc).join(','));
    }));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `email_weekly_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!weeks.length) {
    return <div className="text-sm text-grayscale-1100 py-6 text-center">Nothing was sent in this period with these filters.</div>;
  }
  const th = 'text-left font-grotesk text-xs px-3 py-2 whitespace-nowrap text-grayscale-1200';
  const td = 'px-3 py-1.5 whitespace-nowrap text-grayscale-1300';
  const numTd = `${td} text-right tabular-nums`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1">
          {([['weeks', 'Week by week'], ['grid', 'Template × week grid']] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setLayout(k)}
              className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${layout === k
                ? 'bg-primary-500 text-grayscale-100 font-bold'
                : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>{l}</button>
          ))}
        </div>
        {layout === 'grid' && (
          <Select value={measure} onChange={setMeasure} options={['sent', 'open_rate', 'click_rate', 'ctor']}
            labels={{ sent: 'Show: sent', open_rate: 'Show: open rate', click_rate: 'Show: click rate', ctor: 'Show: CTOR' }} />
        )}
        <span className="text-xs text-grayscale-1100">
          {weeks.length} week{weeks.length === 1 ? '' : 's'} · {rows.length} row{rows.length === 1 ? '' : 's'}
        </span>
        <span className="flex-1" />
        <Btn small onClick={downloadCsv} title="These rows as CSV (the Excel download above has every sheet)">Download CSV</Btn>
      </div>

      {layout === 'weeks' ? (
        <div className="overflow-auto rounded-lg border border-grayscale-300" style={{ maxHeight: '40rem' }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-grayscale-200 z-10">
              <tr>{COLS.map((c, i) => <th key={c} title={COL_HINT[c]} className={`${th} ${i >= 4 ? 'text-right' : ''}`}>{c}</th>)}</tr>
            </thead>
            {weeks.map(w => {
              const list = byWeek.get(w.week) || [];
              const t = totalOf(w.week);
              const part = partial(w.week);
              return (
                <tbody key={w.week} className="border-t-2 border-grayscale-300">
                  <tr className="bg-primary-100">
                    <td className={`${td} font-bold`} colSpan={4}>
                      {w.label || weekLabel(w.week)}
                      {part && <span className="ml-1 text-[11px] font-normal text-grayscale-1100"
                        title="Only part of this week is inside the period (or the week is still running)">· part week</span>}
                      <span className="ml-2 text-[11px] font-normal text-grayscale-1100">
                        {list.length ? `all e-mails · ${list.length} row${list.length === 1 ? '' : 's'}` : 'nothing sent'}
                      </span>
                    </td>
                    <td className={`${numTd} font-bold`}><N v={t.sent} /></td>
                    <td className={`${numTd} font-bold`}><N v={t.delivered} /></td>
                    <td className={`${numTd} font-bold`}><Pct v={rateOf(t, 'open_rate')} /></td>
                    <td className={`${numTd} font-bold`}><Pct v={rateOf(t, 'click_rate')} /></td>
                    <td className={`${numTd} font-bold`}><Pct v={rateOf(t, 'ctor')} /></td>
                    <td className={`${numTd} font-bold`}><N v={t.cta_clicks} /></td>
                    <td className={`${numTd} font-bold`}><N v={t.auto_opened} /></td>
                    <td className={`${numTd} font-bold`}><N v={t.bot_clicked} /></td>
                  </tr>
                  {list.map((r, i) => (
                    <tr key={`${r.template_key}:${r.mode}:${str(r.send_id)}:${i}`} className="border-t border-grayscale-200 hover:bg-grayscale-100">
                      <td className={td}>
                        {onTemplate ? (
                          <button type="button" onClick={() => onTemplate(r.template_key)}
                            title={`${r.template_key} — click to narrow the dashboard to this e-mail`}
                            className="text-left hover:underline underline-offset-2 truncate max-w-[18rem]">{nameOf(r)}</button>
                        ) : <span title={r.template_key}>{nameOf(r)}</span>}
                        {r.trigger_id && <span className="ml-1 text-[10px] text-grayscale-1100" title="MEMAC trigger">{r.trigger_id}</span>}
                        {campaignLink(r)}
                      </td>
                      <td className={`${td} text-xs text-grayscale-1200`}>{categoryWord(r.category)}</td>
                      <td className={td}><CtaCell row={r} /></td>
                      <td className={td}><ModePill mode={r.mode} /></td>
                      <td className={numTd}><N v={r.sent} /></td>
                      <td className={numTd}><N v={r.delivered} /></td>
                      <td className={numTd}><Pct v={rateOf(r, 'open_rate')} /></td>
                      <td className={numTd}><Pct v={rateOf(r, 'click_rate')} /></td>
                      <td className={numTd}><Pct v={rateOf(r, 'ctor')} /></td>
                      <td className={numTd}><CtaClicks row={r} /></td>
                      <td className={numTd}><N v={r.auto_opened} /></td>
                      <td className={numTd}><N v={r.bot_clicked} /></td>
                    </tr>
                  ))}
                </tbody>
              );
            })}
          </table>
        </div>
      ) : (
        <Grid weeks={[...weeks].reverse()} rows={rows} measure={measure} nameOf={nameOf}
          partial={partial} onTemplate={onTemplate} totalOf={totalOf} />
      )}
      <div className="text-[11px] text-grayscale-1100">
        Weeks start on Monday, India time. Open rate = opened (or clicked) ÷ delivered; click rate = clicked ÷
        delivered; CTOR = clicked ÷ opened. "n/a" = a plain-text e-mail, whose opens SendGrid cannot see.
        CTA clicks = clicks on the e-mail's main button. Opens and clicks are people's unless "Include automated" is
        on; auto opens / bot clicks = sends opened only by machines / clicked only by link scanners.
      </div>
    </div>
  );
};

/** Template per row, week per column (oldest left), one measure. */
const Grid: React.FC<{
  weeks: WeekHead[]; rows: WeeklyRow[]; measure: string; nameOf: (r: any) => string;
  partial: (w: string) => boolean; onTemplate?: (k: string) => void; totalOf: (w: string) => Record<string, any>;
}> = ({ weeks, rows, measure, nameOf, partial, onTemplate, totalOf }) => {
  // One line per template and mode: a template sent both by hand and by the
  // system in the same weeks reads as two lines, as in the week view.
  const lines = useMemo(() => {
    const m = new Map<string, { key: string; mode: string; name: string; cells: Map<string, WeeklyRow[]>; sent: number }>();
    rows.forEach(r => {
      const id = `${r.template_key}|${str(r.mode)}`;
      if (!m.has(id)) m.set(id, { key: r.template_key, mode: str(r.mode), name: nameOf(r), cells: new Map(), sent: 0 });
      const l = m.get(id)!;
      const w = r.week.slice(0, 10);
      if (!l.cells.has(w)) l.cells.set(w, []);
      l.cells.get(w)!.push(r);
      l.sent += num(r.sent);
    });
    return Array.from(m.values()).sort((a, b) => b.sent - a.sent);
  }, [rows, nameOf]);
  const cell = (list: WeeklyRow[] | undefined) => {
    if (!list || !list.length) return <span className="text-grayscale-600">·</span>;
    const s = list.length === 1 ? list[0] : sumWeek(list);
    return measure === 'sent' ? <N v={s.sent} /> : <Pct v={rateOf(s, measure)} />;
  };
  const totalCell = (list: WeeklyRow[]) => {
    const s = sumWeek(list);
    return measure === 'sent' ? <N v={s.sent} /> : <Pct v={rateOf(s, measure)} />;
  };
  const th = 'font-grotesk text-xs px-2 py-2 whitespace-nowrap text-grayscale-1200';
  return (
    <div className="overflow-auto rounded-lg border border-grayscale-300" style={{ maxHeight: '40rem' }}>
      <table className="text-sm">
        <thead className="sticky top-0 bg-grayscale-200 z-10">
          <tr>
            <th className={`${th} text-left sticky left-0 bg-grayscale-200`}>Template</th>
            <th className={`${th} text-left`}>Mode</th>
            {weeks.map(w => (
              <th key={w.week} className={`${th} text-right`} title={w.label || weekLabel(w.week)}>
                {weekLabel(w.week).replace('w/c ', '')}{partial(w.week) ? '*' : ''}
              </th>
            ))}
            <th className={`${th} text-right`}>Period</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-primary-100 font-bold">
            <td className="px-2 py-1.5 whitespace-nowrap sticky left-0 bg-primary-100">All e-mails</td>
            <td />
            {weeks.map(w => {
              const t = totalOf(w.week);
              return (
                <td key={w.week} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                  {measure === 'sent' ? <N v={t.sent} /> : <Pct v={rateOf(t, measure)} />}
                </td>
              );
            })}
            <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{totalCell(rows)}</td>
          </tr>
          {lines.map(l => (
            <tr key={`${l.key}|${l.mode}`} className="border-t border-grayscale-200 hover:bg-grayscale-100">
              <td className="px-2 py-1.5 whitespace-nowrap sticky left-0 bg-white max-w-[16rem] truncate">
                {onTemplate ? (
                  <button type="button" onClick={() => onTemplate(l.key)} title={`${l.key} — click to narrow the dashboard to it`}
                    className="text-left hover:underline underline-offset-2">{l.name}</button>
                ) : l.name}
              </td>
              <td className="px-2 py-1.5"><ModePill mode={l.mode} /></td>
              {weeks.map(w => (
                <td key={w.week} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{cell(l.cells.get(w.week))}</td>
              ))}
              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap font-medium">
                {totalCell(Array.from(l.cells.values()).flat())}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WeeklyTable;
